import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { colors } from '../../utils/colors';
import { useKeyboardNavigation } from '../../utils/accessibility';
import logoName from '../../assets/logo_name.png';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${colors.white};
  box-shadow: 0 2px 10px ${colors.shadow};
  z-index: 100;
  padding: 0.5rem 1rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;

  img {
    height: 50px;
    width: auto;
  }
`;

const NavContent = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${colors.textDark};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavLinks = styled(motion.div)`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
    &.open {
      display: flex;
      flex-direction: column;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: ${colors.white};
      padding: 1rem;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
  }
`;

const NavLink = styled(motion.a)`
  color: ${colors.textDark};
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem;
  border-radius: 4px;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.primary};
  }

  &[aria-current="page"] {
    color: ${colors.primary};
  }
`;

export const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);
  const menuButtonRef = useRef(null);
  const linkRefs = useRef([]);

  const links = [
    { href: '#inicio', text: 'Inicio' },
    { href: '#sobre-mi', text: 'Sobre Mí' },
    { href: '#servicios', text: 'Servicios' },
    { href: '#faq', text: 'FAQ' },
    { href: '#contacto', text: 'Contacto' },
    { href: '#testimonios', text: 'Testimonios' },
  ];

  const handleKeyPress = (e) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      menuButtonRef.current?.focus();
    }
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  useKeyboardNavigation(linkRefs.current);

  return (
    <Nav ref={navRef} onKeyDown={handleKeyPress}>
      <Container>
        <Logo href="#inicio">
          <img src={logoName} alt="Andrea Dávila Alpuy - Psicóloga" />
        </Logo>

        <NavContent>
          <MenuButton
            ref={menuButtonRef}
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-controls="nav-links"
            aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'}`} aria-hidden="true" />
          </MenuButton>

          <NavLinks
            id="nav-links"
            className={isOpen ? 'open' : ''}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {links.map((link, index) => (
              <NavLink
                key={link.href}
                href={link.href}
                onClick={handleLinkClick}
                ref={(el) => (linkRefs.current[index] = el)}
              >
                {link.text}
              </NavLink>
            ))}
          </NavLinks>
        </NavContent>
      </Container>
    </Nav>
  );
};
